// How the SSO pages write dates, counts and server refusals: one place, so
// every page reads the same.
import { dateTimeText } from '../date/local-time'

// The figure shown for a count that is missing (undefined, null, blank or
// not a number): never "0", which would read as a real zero.
export const MISSING_COUNT = '—'

// A count, always with thousands separators ("52,679"), rounded to a whole
// number; a missing one reads MISSING_COUNT. The one count formatter the SSO
// and audit screens (and the grid footer) share.
export const formatCount = v => {
  if (v === null || v === undefined || (typeof v === 'string' && !v.trim())) return MISSING_COUNT
  const n = Number(v)
  return Number.isFinite(n) ? Math.round(n).toLocaleString('en-US') : MISSING_COUNT
}

export const plural = (n, word, many = `${word}s`) => `${formatCount(n)} ${Number(n) === 1 ? word : many}`

export const formatDate = value =>
  value ? new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : ''

// An absolute time: the viewer's own, with its zone ("Oct 8, 2026 at
// 11:20 AM MDT"; shared/date/local-time.js).
export const formatDateTime = value => (value ? dateTimeText(value) : '')

const MINUTE = 60 * 1000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

// "just now", "5 minutes ago", "3 hours ago", "2 days ago", else the date.
export const formatAgo = (value, now = Date.now()) => {
  if (!value) return ''
  const at = new Date(value).getTime()
  if (Number.isNaN(at)) return ''
  const diff = now - at
  if (diff < MINUTE) return 'just now'
  if (diff < HOUR) return `${plural(Math.floor(diff / MINUTE), 'minute')} ago`
  if (diff < DAY) return `${plural(Math.floor(diff / HOUR), 'hour')} ago`
  if (diff < 14 * DAY) return `${plural(Math.floor(diff / DAY), 'day')} ago`
  return formatDate(value)
}

// When someone last signed in to an organization, the way a person says it:
// "Just now", "5 minutes ago", "3 hours ago", "Yesterday", "6 days ago",
// "A week ago", "3 weeks ago", "A month ago", "2 months ago", "Over a year
// ago", and "Not opened yet" for no sign-in at all (owner copy 2026-09-27). Unlike formatAgo it never
// turns into a date: an organization chooser compares rows at a glance.
// Intl.RelativeTimeFormat says "last week" / "1 year ago" and has no "over",
// so the copy is written out here. Under a day it counts hours; from there on
// it counts calendar days on this device's clock, so "Yesterday" is the
// previous date, not "24 to 48 hours ago". A time in the future (clock skew)
// is "Just now".
const startOfDay = ms => {
  const d = new Date(ms)
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}

export const NOT_OPENED = 'Not opened yet'

export const formatLastSignedIn = (value, now = Date.now()) => {
  if (!value) return NOT_OPENED
  const at = new Date(value).getTime()
  if (Number.isNaN(at)) return NOT_OPENED
  const diff = now - at
  if (diff < MINUTE) return 'Just now'
  if (diff < HOUR) return `${plural(Math.floor(diff / MINUTE), 'minute')} ago`
  if (diff < DAY) return `${plural(Math.floor(diff / HOUR), 'hour')} ago`
  // Round: a daylight-saving change makes one calendar day 23 or 25 hours.
  const days = Math.max(1, Math.round((startOfDay(now) - startOfDay(at)) / DAY))
  if (days < 2) return 'Yesterday'
  if (days < 7) return `${days} days ago`
  if (days < 14) return 'A week ago'
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`
  if (days < 60) return 'A month ago'
  if (days < 365) return `${Math.min(11, Math.floor(days / 30))} months ago`
  return 'Over a year ago'
}

// "1.2 s", "340 ms", "2 min 5 s".
export const formatDuration = ms => {
  if (ms === null || ms === undefined || Number.isNaN(Number(ms))) return ''
  const n = Number(ms)
  if (n < 1000) return `${Math.round(n)} ms`
  if (n < 60000) return `${(n / 1000).toFixed(n < 10000 ? 1 : 0)} s`
  const minutes = Math.floor(n / 60000)
  const seconds = Math.round((n % 60000) / 1000)
  return seconds ? `${minutes} min ${seconds} s` : `${minutes} min`
}

// The server's own sentence when it sent one (every SSO endpoint writes its
// refusals for the person reading), else the page's fallback.
export const serverMessage = (err, fallback) =>
  (err && err.data && typeof err.data.message === 'string' && err.data.message) ||
  (err && err.error && typeof err.error.message === 'string' && err.error.message) ||
  fallback
